<template>
  <v-row
    align="center"
    class="dashboard-filters"
    dense
  >
    <v-col cols="auto">
      <v-select
        v-model="rangeOption"
        dense
        hide-details
        :items="rangeOptions"
        :label="$t('labels.dateRange')"
        outlined
        style="width: 220px"
      />
    </v-col>
    <v-col
      v-if="rangeOption === 'custom'"
      cols="auto"
    >
      <v-menu
        v-model="showCustomRange"
        :close-on-content-click="false"
        min-width="auto"
        offset-y
      >
        <template #activator="{ on, attrs }">
          <v-text-field
            class="custom-range"
            dense
            hide-details
            outlined
            readonly
            style="width: 260px"
            :value="customRangeText"
            v-bind="attrs"
            v-on="on"
          />
        </template>
        <v-date-picker
          v-model="customRangeDraft"
          no-title
          range
          @change="applyCustomRange"
        />
      </v-menu>
    </v-col>
    <v-col
      v-if="!isDepartmentManagement"
      cols="auto"
    >
      <v-select
        v-model="departmentIds"
        class="departments"
        clearable
        dense
        hide-details
        item-text="name"
        item-value="id"
        :items="departments"
        :label="$tc('labels.department', 2)"
        multiple
        outlined
        :placeholder="$t('labels.all')"
        style="width: 260px"
      />
    </v-col>
    <v-spacer />
    <v-col
      class="range-text body-2 grey--text text--darken-1"
      cols="auto"
    >
      {{ rangeText }}
    </v-col>
  </v-row>
</template>

<script>
import _ from 'lodash';
import moment from 'moment';
import { namedValues } from '@/components/scheduling/reports/columns';
import { getRelativeDateRange, RELATIVE_DATE_FILTERS } from '@/utils/scheduling';

// The API's longest range. Using 366 days to account for leap years.
const MAX_RANGE_DAYS = 366;

/**
 * Date range and department filters for the report dashboards. Emits `input` with `{ start, end, departmentIds }`
 * when mounted and whenever the filters change. Department management can't pick departments: the API limits them
 * to their own.
 */
export default {
  data () {
    const schedulingSettings = _.get(this.$store.state.org, ['settings', 'scheduling'], {});
    return {
      customRange: [],
      customRangeDraft: [],
      departmentIds: [],
      emittedKey: null,
      // The current schedule period, unless the hospital has none.
      rangeOption: getRelativeDateRange('currentPeriod', schedulingSettings) ? 'currentPeriod' : 'thisMonth',
      showCustomRange: false
    };
  },
  computed: {
    criteria () {
      if (!this.range) {
        return null;
      }
      return {
        departmentIds: this.departmentIds || [],
        end: this.range.end,
        start: this.range.start
      };
    },
    criteriaKey () {
      return JSON.stringify(this.criteria);
    },
    customRangeText () {
      if (this.customRange.length === 0) {
        return '';
      }
      return this.formatRange(this.customRange[0], this.customRange[this.customRange.length - 1]);
    },
    departments () {
      return namedValues(this.$store.state.org.departments);
    },
    isDepartmentManagement () {
      return this.$store.getters['account/isDepartmentManagement']();
    },
    range () {
      if (this.rangeOption === 'custom') {
        if (this.customRange.length === 0) {
          return null;
        }
        const [start, end] = [...this.customRange].sort();
        const latestStart = moment(end || start).subtract(MAX_RANGE_DAYS, 'days').format('YYYY-MM-DD');
        return {
          start: start < latestStart ? latestStart : start,
          end: end || start
        };
      }
      return getRelativeDateRange(this.rangeOption, this.schedulingSettings);
    },
    rangeOptions () {
      const options = RELATIVE_DATE_FILTERS
        .filter(key => getRelativeDateRange(key, this.schedulingSettings))
        .map(key => ({ text: this.$t(`labels.${key}`), value: key }));
      options.push({ text: this.$t('labels.custom'), value: 'custom' });
      return options;
    },
    rangeText () {
      return this.range ? this.formatRange(this.range.start, this.range.end) : '';
    },
    schedulingSettings () {
      return _.get(this.$store.state.org, ['settings', 'scheduling'], {});
    }
  },
  watch: {
    criteriaKey () {
      this.emitCriteria();
    },
    rangeOption (value, oldValue) {
      if (value === 'custom' && this.customRange.length === 0) {
        // Start from the range that was showing, so switching to "Custom" doesn't change the criteria.
        const previousRange = getRelativeDateRange(oldValue, this.schedulingSettings);
        if (previousRange) {
          this.customRange = [previousRange.start, previousRange.end];
          this.customRangeDraft = [...this.customRange];
        }
      }
    }
  },
  mounted () {
    this.emitCriteria();
  },
  methods: {
    applyCustomRange (dates) {
      if (dates.length === 2) {
        this.customRange = [...dates].sort();
        this.showCustomRange = false;
      }
    },
    emitCriteria () {
      // The criteria can be briefly incomplete and come back unchanged, e.g. while switching to "Custom".
      if (this.criteria && this.criteriaKey !== this.emittedKey) {
        this.emittedKey = this.criteriaKey;
        this.$emit('input', this.criteria);
      }
    },
    formatRange (start, end) {
      const format = this.$store.getters['org/getDateFormatLong']();
      const startText = moment(start).format(format);
      return start === end ? startText : `${startText} - ${moment(end).format(format)}`;
    }
  }
};
</script>

<style lang="scss">
.dashboard-filters {
  padding: 4px 0 8px;
}
</style>
